import {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useStore} from 'react-redux'
import {readEndpoint} from 'redux-json-api'
import PropTypes from 'prop-types'

import EnrichInterviewFooter from './EnrichInterviewFooter'
import EnrichInterviewView from './EnrichInterviewView'
import EnrichTakeover from './EnrichTakeover'
import RefineDiffView from './RefineDiffView'
import RefineFooter from './RefineFooter'
import RefineMenu, {isSheetLayout} from './RefineMenu'
import RefineNote from './RefineNote'
import RecordVersionModal from './RecordVersionModal'
import SaveIndicator from './SaveIndicator'
import StoryPhotoPrints from './StoryPhotoPrints'
import StoryPhotoViewer from './StoryPhotoViewer'
import StuckOverlay from './StuckOverlay'
import useAnchoredChapterLabel from './hooks/useAnchoredChapterLabel'
import useAutosave from './hooks/useAutosave'
import useEnrichInterview, {ENRICHED_LABEL, INTERVIEW_NOTES, INTERVIEW_PHASE, INTERVIEW_STEP} from './hooks/useEnrichInterview'
import {SAVE_STATUS} from './hooks/useLifeStoryDraft'
import useRecordVersion, {RECORD_VERSION_COPY} from './hooks/useRecordVersion'
import useRefineStory, {
  countWords,
  LIFE_STORY_REFINE_FLAG,
  MIN_SELECTION_WORDS,
  REFINE_MODES,
  REFINE_STATUS
} from './hooks/useRefineStory'
import useStoryPhotoImages, {useFullStoryPhotos} from './hooks/useStoryPhotoImages'
import useStoryPhotos, {MAX_STORY_PHOTOS, STORY_PHOTO_ACCEPT, STORY_PHOTO_MESSAGES} from './hooks/useStoryPhotos'
import useVoiceRecorder, {isLiveRecorderState, RECORDER_STATES} from './hooks/useVoiceRecorder'
import {CaretIcon, MicIcon, PhotoIcon, PlayIcon, PauseIcon, RedoIcon, UndoIcon} from './icons'
import {API_MODES, refineLifeStory} from './lib/refineApi'
import {LIFE_STORY_CHAPTERS} from './lifeStoryContent'
import {formatDuration, MAX_STORY_LENGTH} from './lifeStoryTools'
import {trackLifeStoryEvent} from './lifeStoryEventLogger'
import FeatureFlags from '../../../../../lib/FeatureFlags'
import {touchSessionActivity} from '../../../../../lib/sessionActivity'

import './promptScreen.scss'

const TrashIcon = () => (
  <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='M4 7h16M9 7V5h6v2M6.5 7l1 13h9l1-13M10 11v5M14 11v5' />
  </svg>
)

// How long a cap-related message stays up: the recorder auto-stopping at the
// 10-minute or 25,000-character limit, or recording being blocked because the
// answer is already at the character limit. Longer than the default toast:
// these are the only explanation the user gets for the recorder acting on its
// own, and testers couldn't finish reading them at 4 seconds, let alone the
// default duration (TK-3017).
const CAP_MESSAGE_DURATION_MS = 8000

// A selection made in the textarea counts for the refine menu's scope when
// the wand is opened this soon after the textarea lost focus (AC 2.5) — long
// enough to cover a click on the wand, short enough that an old selection
// from minutes ago doesn't silently narrow a refine.
const SELECTION_WINDOW_MS = 400

// Every chapter's name, so the model can tell what belongs to this question
// and what another chapter will ask about.
const CHAPTER_NAMES = LIFE_STORY_CHAPTERS.map(chapter => chapter.name)

// The single-pass refines end their review bar with Done (prototype 0928
// gives it to Polish and Shorten; a custom request is the same kind of
// pass, so it gets one too). The interview's finishing rewrite keeps the
// bar as it is: its done screen already closes that flow.
const DONE_MODES = [REFINE_MODES.POLISH, REFINE_MODES.SHORTEN, REFINE_MODES.CUSTOM]

// A mouse or trackpad: the takeover can suggest Enter to continue.
const hasFinePointer = () => typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches

// The textarea's resize grip sits in its bottom-right corner (browsers draw
// it about 16px square). A pointer landing there is grabbing the grip, not
// clicking into the story.
const RESIZE_GRIP_SIZE = 20

const onResizeGrip = (element, event) => {
  if (!event || typeof event.clientX !== 'number' || typeof event.clientY !== 'number') return false

  const rect = element.getBoundingClientRect()

  return rect.right - event.clientX <= RESIZE_GRIP_SIZE && rect.bottom - event.clientY <= RESIZE_GRIP_SIZE
}

/**
 * One question: write or speak an answer, with live transcription (mock stages 3–4).
 *
 * The answer autosaves to the server while the user writes or dictates
 * (TK-3179): shortly after they pause, at least every 15 seconds while they
 * don't, and immediately when they leave the text box, hide the tab, or take
 * any way out of the screen (Next, Save and close, chapter switch) except
 * Back, which drops what hasn't saved yet (TK-3178). A finished recording
 * saves the moment its upload lands.
 *
 * "Refine" (TK-3204) rewrites the written answer through the API on
 * request; the refined text is applied here and persisted by the same
 * autosave, and the recording is never touched. Its Enrich option is an
 * interview instead of a rewrite: a few questions asked over the story, then
 * one finishing rewrite the writer can use or leave.
 *
 * "Record this version" records the refined story read aloud (word for word
 * or from talking points). A first recording becomes the story's; over an
 * existing one the new take is saved as a draft until the writer chooses,
 * and the replaced one stays only while the note's Undo is up.
 *
 * The card has one record button (TK-3234). Its label and what it opens
 * follow the story's state: "Speak it instead" (no recording) and "Say it
 * again" (a recording, not refined since) run the inline dictation
 * recorder; "Record this version" (a recording, and a refine applied since
 * it was made) opens the read-along recorder above. The review bar keeps
 * its own "Record this version" while a result is up for review.
 */
const PromptScreen = props => {
  const {chapter, prompt, story} = props
  const dispatch = useDispatch()
  const store = useStore()

  // Coalesced so a story saved without text (voice-only answers) can't seed
  // null into everything that treats `text` as a string — the atMaxLength
  // render check and the .trim() calls in the finish handlers alike.
  const [text, setText] = useState((story && story.text) || '')
  const [interimText, setInterimText] = useState('')
  const [recording, setRecording] = useState(story ? story.recording : null)
  const [draftRecording, setDraftRecording] = useState((story && story['draft-recording']) || null)
  // A replaced recording is kept only while the Undo on its note is up, so
  // one left over from an earlier visit isn't loaded: the next save drops it.
  const [replacedRecording, setReplacedRecording] = useState(null)
  const [showStuckOverlay, setShowStuckOverlay] = useState(false)
  const [confirmingReplace, setConfirmingReplace] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [playbackElapsed, setPlaybackElapsed] = useState(0)

  // The ice-breakers show while the answer is empty and come back if it is
  // emptied out (TK-3204 AC 1.5), replacing TK-3179's once-per-visit
  // decision. Hiding on the first keystroke happens while the writer is
  // typing, so it can't move Next out from under a click the way the old
  // hide-on-autosave did.
  const showStarters = Boolean(prompt.starters && prompt.starters.length > 0 && text.trim() === '')

  const textRef = useRef(text)
  // Mirrors `recording` for saves that fire from timers and lifecycle events:
  // a closure over the state could carry the recording from before a
  // replacement, and a payload missing the new upload has the API delete it.
  const recordingRef = useRef(recording)
  const draftRecordingRef = useRef(draftRecording)
  const replacedRecordingRef = useRef(replacedRecording)
  // The story's photos (TK-3108), mirrored for the same reason: every save
  // carries them, and a payload missing one has the API delete its upload.
  const photosRef = useRef((story && story.photos) || [])
  const photoInputRef = useRef(null)
  const addPhotosButtonRef = useRef(null)
  const questionRef = useRef(null)
  const topBarRef = useRef(null)
  const chapterLabelRef = useRef(null)
  const writeCardRef = useRef(null)
  const textareaRef = useRef(null)
  // Back's button and whether it is being pressed: the textarea's blur checks
  // both so the words Back drops aren't flushed on the way to it (TK-3178).
  const backButtonRef = useRef(null)
  const backPressedRef = useRef(false)
  const dictationBaseRef = useRef('')
  const audioRef = useRef(null)
  const audioUrlRef = useRef(null)
  // The in-flight download behind a play click. Play clicked again before the
  // download lands must not start a second one — every response would spawn
  // its own Audio element and they'd all play over each other (TK-3078).
  const pendingPlaybackRef = useRef(null)
  // A revisit's Cancel puts the words back the way Review showed them, even
  // though autosave has been persisting the edits along the way (TK-3061's
  // "nothing was saved" contract, kept for text). Recording changes made
  // during the revisit stay: they were committed on the spot, as before.
  const revisitTextSnapshotRef = useRef((story && story.text) || '')

  textRef.current = text

  useEffect(() => () => {
    // A download still in flight must not start playback after unmount.
    pendingPlaybackRef.current = null

    if (audioRef.current)
      audioRef.current.pause()
  }, [])

  const save = useCallback((overrides = {}, options) => {
    const answer = {
      chapter,
      prompt,
      text: textRef.current,
      recording: recordingRef.current,
      draftRecording: draftRecordingRef.current,
      replacedRecording: replacedRecordingRef.current,
      photos: photosRef.current,
      ...overrides
    }

    return options ? props.saveAnswer(answer, options) : props.saveAnswer(answer)
  }, [props.saveAnswer, chapter, prompt])

  // Autosaves are flagged so the flow knows the writer didn't ask for them
  // (they keep the server current without marking the keepsake for republish).
  const autosave = useAutosave({
    save: () => save({}, {autosave: true}),
    saveOnPageHide: () => save({}, {autosave: true, keepalive: true})
  })

  /* ---------- Photos (TK-3108) ---------- */

  // Adding, removing and reordering photos save on the spot (AC 4.1); this
  // save carries everything a pending autosave would have.
  const savePhotos = useCallback(nextPhotos => {
    photosRef.current = nextPhotos
    autosave.cancel()

    return save({photos: nextPhotos})
  }, [save, autosave.cancel])

  const storyPhotos = useStoryPhotos({
    userConfig: props.userConfig,
    initialPhotos: photosRef.current,
    save: savePhotos,
    toast: props.toast
  })

  // At three the button only looks disabled: a click explains why (AC 1.3).
  const openPhotoPicker = () => {
    if (storyPhotos.atLimit) {
      props.toast(STORY_PHOTO_MESSAGES.atLimit)

      return
    }

    photoInputRef.current.click()
  }

  // After the last photo is removed (AC 3.1): Add photos, or the question
  // when Add photos isn't there to take it (a refine up for review shows its
  // own footer) or is disabled (refining, the interview).
  const focusAfterLastPhotoRemoved = useCallback(() => {
    const addPhotos = addPhotosButtonRef.current

    if (addPhotos && !addPhotos.disabled) addPhotos.focus()
    else if (questionRef.current) questionRef.current.focus()
  }, [])

  const handlePhotoFiles = event => {
    storyPhotos.addFiles(event.target.files)
    // Picking the same file again must still fire a change.
    event.target.value = ''
  }

  const photoImages = useStoryPhotoImages(storyPhotos.photos.map(photo => photo['upload-id']))
  // A print opens larger in the viewer without its Preview controls (AC
  // 3.2): the cover shows at once, the full copy replaces it in place, and
  // closing gives focus back to the print.
  const [viewingPhotoIndex, setViewingPhotoIndex] = useState(null)
  const viewerOpenerRef = useRef(null)
  const viewingPhoto = viewingPhotoIndex === null ? null : storyPhotos.photos[viewingPhotoIndex]
  const fullPhotos = useFullStoryPhotos(viewingPhoto ? [viewingPhoto['upload-id']] : [], {enabled: Boolean(viewingPhoto)})

  const openPhotoViewer = useCallback(index => {
    viewerOpenerRef.current = document.activeElement
    setViewingPhotoIndex(index)
  }, [])

  const closePhotoViewer = useCallback(() => {
    setViewingPhotoIndex(null)

    if (viewerOpenerRef.current && viewerOpenerRef.current.focus) viewerOpenerRef.current.focus()
  }, [])
  const showsPrints = storyPhotos.photos.length + storyPhotos.uploading.length > 0

  useAnchoredChapterLabel({active: showsPrints, labelRef: chapterLabelRef, topRef: topBarRef})

  // On a phone the prints can still push the writing box off screen; after a
  // photo lands, bring it back into view (AC 2.2).
  const landedPhotoCountRef = useRef(storyPhotos.photos.length)

  useEffect(() => {
    const landed = storyPhotos.photos.length > landedPhotoCountRef.current

    landedPhotoCountRef.current = storyPhotos.photos.length

    if (!landed || !window.matchMedia || !window.matchMedia('(max-width: 640px)').matches) return

    const timer = setTimeout(() => {
      const card = writeCardRef.current

      if (card && card.getBoundingClientRect().bottom > window.innerHeight) {
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches

        card.scrollIntoView({block: 'end', behavior: smooth ? 'smooth' : 'auto'})
      }
    }, 120)

    return () => clearTimeout(timer)
  }, [storyPhotos.photos.length])

  // AC 2.4: the prompt speaks to the photos once the story has one.
  const answerPlaceholder = () => {
    if (storyPhotos.photos.length === 0) return 'Start anywhere. The small details are the good part\u2026'

    return storyPhotos.photos.length === 1 ? 'Tell the story behind this photo\u2026' : 'Tell the story behind these photos\u2026'
  }

  /* ---------- Refine (TK-3204) ---------- */

  const refineEnabled = FeatureFlags.isEnabled(LIFE_STORY_REFINE_FLAG)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuSelection, setMenuSelection] = useState(null)
  const capturedSelectionRef = useRef(null)
  const wandRef = useRef(null)
  const stopButtonRef = useRef(null)
  const undoButtonRef = useRef(null)
  // Where focus goes when a refine settles back to idle: the wand after a
  // stop, failure or undo; the end of the text after Keep it; nowhere when
  // the writer is already typing.
  const focusAfterIdleRef = useRef(null)

  // A refined answer, an Undo and a Redo are each a whole edit, so they save
  // at once rather than on the typing debounce (AC 8.1) — and a forced
  // logout right after can't lose them (AC 9.8).
  const applyRefinedText = useCallback(nextText => {
    setText(nextText)
    textRef.current = nextText
    autosave.schedule()
    autosave.flush()
  }, [autosave.schedule, autosave.flush])

  // Every request carries the chapter this question sits in and the other
  // chapters' names, so the model keeps to this question's ground.
  const requestRefine = useCallback((attributes, signal) => {
    const state = store.getState()
    const axiosConfig = state.api && state.api.endpoint && state.api.endpoint.axiosConfig

    return refineLifeStory({attributes: {chapter: chapter.name, chapters: CHAPTER_NAMES, ...attributes}, signal, axiosConfig})
  }, [store, chapter.name])

  const refine = useRefineStory({
    getText: () => textRef.current,
    applyText: applyRefinedText,
    question: prompt.question,
    request: requestRefine,
    hasRecording: Boolean(recording),
    logEvent: props.logEvent
  })

  // Which refine version each take was recorded over, by upload, stamped as
  // the take lands (TK-3234) — so it travels with the take through the
  // recording/draft/replaced slots, and an older take put back (Undo of a
  // replacement, a parked draft used later) keeps the version it was made
  // over. Takes loaded with the story predate this visit's refines, so they
  // read as the version the screen opened on. The current version is
  // mirrored in a ref so the recorders' memoized callbacks can read it.
  const refineVersionRef = useRef(refine.version)
  const openingVersionRef = useRef(refine.version)
  const takeVersionsRef = useRef(new Map())

  refineVersionRef.current = refine.version

  const noteTakeLanded = useCallback(take => {
    takeVersionsRef.current.set(take['upload-id'], refineVersionRef.current)
  }, [])

  const versionOfTake = take => {
    const versions = takeVersionsRef.current

    return versions.has(take['upload-id']) ? versions.get(take['upload-id']) : openingVersionRef.current
  }

  // On a phone the interview takes over the screen instead of sitting in the
  // answer card (prototype "focus" flow). Decided when Enrich is chosen, like
  // the menu's sheet layout.
  const [interviewTakeover, setInterviewTakeover] = useState(false)

  // The interview's woven story lands like any refine result: a version with
  // an Undo behind it, and the note that says to read it over. The takeover
  // stays open on its done screen, so its note comes when it closes instead.
  const useInterviewResult = ({text: nextText, keepOpen}) => {
    refine.applyExternal({
      text: nextText,
      label: ENRICHED_LABEL,
      produced: {mode: API_MODES.ENRICH, scope: 'story'},
      note: keepOpen ? null : {kind: 'info', code: 'used', message: INTERVIEW_NOTES.used}
    })
  }

  // The interview footer's Cancel: where focus lands when the last question
  // is dismissed with nothing answered and the card closes.
  const interviewCancelRef = useRef(null)

  const interview = useEnrichInterview({
    getText: () => textRef.current,
    question: prompt.question,
    chapter: chapter.name,
    chapters: CHAPTER_NAMES,
    request: requestRefine,
    onUse: useInterviewResult,
    onLastDismissed: () => { if (interviewCancelRef.current) interviewCancelRef.current.focus() },
    showNote: refine.showNote,
    announce: refine.announce,
    logEvent: props.logEvent
  })

  const generating = refine.status === REFINE_STATUS.GENERATING
  const interviewing = interview.running

  const handleTextChange = event => {
    setText(event.target.value)
    textRef.current = event.target.value
    autosave.schedule()
    // Typing is the writer's own edit: it closes the review bar without
    // moving focus away from the box.
    focusAfterIdleRef.current = null
    refine.noteTyping()
  }

  // Fires once per recording when dictation fills the answer to its limit;
  // holds the current stop handler (defined below the recorder hook, since it
  // needs stopRecording). handleTranscript reaches it through the ref.
  const stopForLengthRef = useRef(() => {})
  const lengthStoppedRef = useRef(false)

  const handleTranscript = useCallback((finalText, interim) => {
    if (finalText) {
      dictationBaseRef.current = `${dictationBaseRef.current}${finalText}`

      // One answer holds at most MAX_STORY_LENGTH characters (AC 3.9).
      // Dictation appends programmatically, so the textarea's maxLength can't
      // enforce it here — the transcript is cut at the limit and the
      // recording stops through the normal stop flow, so the audio itself
      // uploads and is kept in full.
      if (dictationBaseRef.current.length >= MAX_STORY_LENGTH) {
        dictationBaseRef.current = dictationBaseRef.current.slice(0, MAX_STORY_LENGTH)
        stopForLengthRef.current()
      }

      setText(dictationBaseRef.current)
      textRef.current = dictationBaseRef.current
      // The transcript saves as it appears, not only when the recording
      // finishes (AC 1.3) — the recording itself joins the payload once its
      // upload lands.
      autosave.schedule()
      // Spoken words are the writer's own edit, like typing: a refine note
      // or review bar left up no longer describes the text.
      refine.noteTyping()
    }

    setInterimText(interim)
  }, [autosave.schedule, refine.noteTyping])

  const dropCachedPlayback = useCallback(() => {
    // Any in-flight download belongs to the recording that was just removed
    // or replaced; its audio must not land and play.
    pendingPlaybackRef.current = null

    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
      audioUrlRef.current = null
    }

    setPlaying(false)
    setPlaybackElapsed(0)
  }, [])

  // A recording stopped by the 10-minute cap sets this so the completion
  // toast doesn't replace the cap message moments later — the cap message
  // needs its full display time to be read (TK-3017), and it already promises
  // every word was kept.
  const cappedRecordingRef = useRef(false)

  const handleRecordingComplete = useCallback(newRecording => {
    // Drop any cached playback of the previous recording so the play button
    // fetches and plays the new upload rather than the stale Audio element.
    dropCachedPlayback()

    // The ref is updated before the save so a pending autosave can't be the
    // one to fire with the old recording; the timer is dropped outright since
    // this save carries everything it would have.
    recordingRef.current = newRecording
    noteTakeLanded(newRecording)
    // A dictation take replaces the recording outright. The Record this
    // version Undo slot described the arrangement before it — left in place,
    // its Undo would put that old upload back over this take — and so does
    // any note about a recording, whether or not a transcript came through
    // to clear it as typing would.
    replacedRecordingRef.current = null
    setReplacedRecording(null)
    if (refine.note && String(refine.note.code).startsWith('recording_')) refine.showNote(null)
    autosave.cancel()
    setRecording(newRecording)
    setInterimText('')
    save({recording: newRecording, replacedRecording: null})

    if (cappedRecordingRef.current)
      cappedRecordingRef.current = false
    else
      props.toast('Got every word \u2014 and kept your voice with it.')
  }, [save, props.toast, dropCachedPlayback, autosave.cancel, refine.note, refine.showNote, noteTakeLanded])

  const handleCapReached = useCallback(() => {
    cappedRecordingRef.current = true
    props.toast(
      'You reached the 10-minute recording limit. Every word up to here is kept.',
      {duration: CAP_MESSAGE_DURATION_MS}
    )
  }, [props.toast])

  const announcePause = useCallback(paused => {
    refine.announce(paused ? 'Recording paused.' : 'Recording resumed.')
  }, [refine.announce])

  const {recorderState, starting: recorderStarting, duration, startRecording, pauseRecording, resumeRecording, stopRecording, nearingCap, recordingSupported} = useVoiceRecorder({
    userConfig: props.userConfig,
    question: prompt.question,
    onTranscript: handleTranscript,
    onRecordingComplete: handleRecordingComplete,
    onError: props.toast,
    onCapReached: handleCapReached,
    onPauseChange: announcePause
  })

  // Reassigned every render rather than memoized: it needs the stopRecording
  // just returned above. Reuses the cap flag so the completion toast doesn't
  // replace the limit message (TK-3017's reading-time fix).
  stopForLengthRef.current = () => {
    if (lengthStoppedRef.current) return

    lengthStoppedRef.current = true
    cappedRecordingRef.current = true
    props.toast(
      'This story reached its 25,000-character limit. Your recording and every word up to here are saved.',
      {duration: CAP_MESSAGE_DURATION_MS}
    )
    stopRecording()
  }

  const beginRecording = () => {
    const existingText = textRef.current.trim()

    // Clamped so the "\n\n" separator can't push the seed past the limit on
    // its own — the length stop should be driven by transcript, not padding.
    dictationBaseRef.current = (existingText ? `${existingText}\n\n` : '').slice(0, MAX_STORY_LENGTH)
    // A stale flag from a capped recording whose upload failed shouldn't
    // silence this recording's completion toast.
    cappedRecordingRef.current = false
    lengthStoppedRef.current = false
    startRecording()
  }

  const toggleRecording = () => {
    // The mic is hidden during the Enrich interview; nothing that still
    // reaches this (a stale handler, a shortcut) may start dictating into it.
    if (interview.running) return

    if (isLiveRecorderState(recorderState)) {
      stopRecording()
    } else if (recorderState === RECORDER_STATES.IDLE) {
      // A full answer can't take new transcription, and a recording that
      // stops itself after one sentence would only confuse — explain instead.
      if (textRef.current.length >= MAX_STORY_LENGTH) {
        props.toast(
          'This story is at its 25,000-character limit. Trim some text to add a recording.',
          {duration: CAP_MESSAGE_DURATION_MS}
        )

        return
      }

      // One voice attachment per story: recording over an existing one needs
      // the mock's replace confirmation first.
      if (recording)
        setConfirmingReplace(true)
      else
        beginRecording()
    }
  }

  // The existing recording stays until the new one actually lands
  // (handleRecordingComplete): startRecording can fail before recording
  // anything (no mic permission, unsupported browser), and the user's saved
  // voice shouldn't be lost to a replacement that never happened.
  const confirmReplace = () => {
    setConfirmingReplace(false)
    dropCachedPlayback()
    beginRecording()
  }

  /* ---------- Record this version (TK-3204) ---------- */

  // Moves recordings between the story's three slots and saves at once; the
  // refs go first so no timer-driven save can carry the old arrangement.
  const commitRecordings = next => {
    if ('recording' in next) {
      recordingRef.current = next.recording
      setRecording(next.recording)
    }

    if ('draft' in next) {
      draftRecordingRef.current = next.draft
      setDraftRecording(next.draft)
    }

    if ('replaced' in next) {
      replacedRecordingRef.current = next.replaced
      setReplacedRecording(next.replaced)
    }

    autosave.cancel()

    return save()
  }

  // Removing the voice attachment takes every recording slot with it: the
  // draft take only ever stood in relation to the recording it might have
  // replaced, and the replaced one (behind the note's Undo) must not be able
  // to come back once the writer has said no recording at all. Any note
  // about a recording ("saved", "replaces", "is back", "kept", "waiting")
  // describes something that no longer exists, so it goes too.
  const removeRecording = () => {
    dropCachedPlayback()
    if (refine.note && String(refine.note.code).startsWith('recording_')) refine.showNote(null)
    commitRecordings({recording: null, draft: null, replaced: null})
    props.toast('Voice attachment removed. Your written words are kept.')
  }

  // The read-along recorder's takes land here (a first one as the story's
  // recording, later ones as the draft); each is stamped with the version it
  // read from, before Use new / Keep current / Undo move it between slots.
  const saveFirstVersion = (take, {quiet}) => {
    noteTakeLanded(take)
    dropCachedPlayback()
    commitRecordings({recording: take})
    if (!quiet) refine.showNote({kind: 'info', code: 'recording_saved', message: RECORD_VERSION_COPY.notes.saved})
  }

  const draftVersion = take => {
    noteTakeLanded(take)

    return commitRecordings({draft: take})
  }

  // Undo puts the previous recording back and parks the new take as the
  // draft, so neither is lost to a change of mind.
  const undoReplace = () => {
    const previous = replacedRecordingRef.current
    const take = recordingRef.current

    if (!previous) return

    dropCachedPlayback()
    commitRecordings({recording: previous, draft: take, replaced: null})
    refine.showNote({kind: 'info', code: 'recording_restored', message: RECORD_VERSION_COPY.notes.undone})
    refine.announce(RECORD_VERSION_COPY.announce.undone)
  }

  // The replaced recording is kept only behind the note's Undo; in the
  // takeover there is no Undo, so it goes at once.
  const useNewVersion = (take, {quiet}) => {
    const previous = recordingRef.current

    dropCachedPlayback()
    commitRecordings({recording: take, draft: null, replaced: quiet ? null : previous})

    if (!quiet) {
      refine.showNote({
        kind: 'info',
        code: 'recording_replaced',
        message: RECORD_VERSION_COPY.notes.replaced,
        // Named apart from the review bar's Undo, which may be up at the same time.
        action: {label: 'Undo', ariaLabel: 'Undo replacing the recording', onAction: undoReplace}
      })
    }
  }

  const keepCurrentVersion = ({quiet}) => {
    commitRecordings({draft: null})
    if (!quiet) refine.showNote({kind: 'info', code: 'recording_kept', message: RECORD_VERSION_COPY.notes.kept})
  }

  const parkDraftVersion = () => {
    refine.showNote({kind: 'info', code: 'recording_parked', message: RECORD_VERSION_COPY.notes.parked})
  }

  // Once the note with Undo is gone (typing, another note, a refine), the
  // replaced recording has nothing to come back through.
  useEffect(() => {
    if (!replacedRecording || (refine.note && refine.note.code === 'recording_replaced')) return

    commitRecordings({replaced: null})
  }, [replacedRecording, refine.note])

  const handleVersionCapReached = useCallback(() => {
    props.toast('You reached the 10-minute recording limit. Everything up to here is kept.', {duration: CAP_MESSAGE_DURATION_MS})
  }, [props.toast])

  const recordVersion = useRecordVersion({
    getText: () => textRef.current,
    question: prompt.question,
    recording,
    userConfig: props.userConfig,
    request: requestRefine,
    // The card's playback must not run under the recorder (see the hook).
    onBegin: dropCachedPlayback,
    onSaveFirst: saveFirstVersion,
    onDraft: draftVersion,
    onUseNew: useNewVersion,
    onKeepCurrent: keepCurrentVersion,
    onParkDraft: parkDraftVersion,
    onError: props.toast,
    onCapReached: handleVersionCapReached,
    onPauseChange: announcePause,
    announce: refine.announce
  })

  const applyStarter = starter => {
    trackLifeStoryEvent({eventName: 'used_starter', userConfig: props.userConfig})

    const base = textRef.current.trim()
    const nextText = `${base ? `${base}\n\n` : ''}${starter.replace('\u2026', ' ')}`

    setText(nextText)
    textRef.current = nextText
    autosave.schedule()

    const textarea = textareaRef.current

    if (textarea) {
      textarea.focus()
      // After React flushes the new value, put the caret at the end so the
      // user can keep writing from where the starter leaves off.
      requestAnimationFrame(() => textarea.setSelectionRange(nextText.length, nextText.length))
    }
  }

  // Leaving the screen while a refine is up for review keeps it: the text is
  // already applied and saved, so only the decision needs recording.
  // The recording behind the replaced note's Undo lives only as long as
  // that note; on the way out it is dropped before the exit's own save, so
  // the old upload is saved as gone (the API reaps it) rather than kept
  // behind an Undo that is no longer reachable.
  const dropReplacedRecording = () => {
    replacedRecordingRef.current = null
    setReplacedRecording(null)
  }

  const leaveReview = () => {
    focusAfterIdleRef.current = null
    refine.keep({implicit: true})
    dropReplacedRecording()
  }

  // Every explicit way out saves on its own, so the pending autosave is
  // dropped first: left armed, it would fire after the exit with a payload
  // that no longer matches (e.g. flipping a Skip back to unskipped).
  const skip = () => {
    leaveReview()
    autosave.cancel()
    save({skipped: true})
    props.advance({answered: false})
  }

  // Back is neutral (TK-3178): nothing is saved or marked skipped, and the
  // words typed since the last autosave are dropped with the pending save. A
  // refine still up for review is kept like on every other way out: its text
  // is already applied and autosaved.
  //
  // The one thing Back does write: a recording left behind the replaced
  // note's Undo. "Use new recording" saved that slot, so dropping it locally
  // isn't enough — the old upload would stay on the server behind an Undo
  // nobody can reach. The removal goes out with the words the server already
  // has (`story.text`, kept current by autosave), not the unsaved tail.
  const back = () => {
    const hadReplacedRecording = Boolean(replacedRecordingRef.current)

    backPressedRef.current = false
    leaveReview()
    autosave.cancel()

    if (hadReplacedRecording)
      save({text: (story && story.text) || ''}, {autosave: true})

    props.back()
  }

  const next = () => {
    // Next with nothing added is treated as a skip (AC 3.8) — no
    // "write a little first" nag. A recording with no transcribed text, or a
    // photo on its own, still counts as an answer (TK-3108 AC 4.3).
    if (!textRef.current.trim() && !recording && photosRef.current.length === 0) {
      skip()

      return
    }

    leaveReview()
    autosave.cancel()
    save({}, {finished: true})
    props.advance({answered: true})
  }

  // An answer emptied out since the last autosave is still a change worth
  // saving — otherwise the erased words would be back on the next visit.
  const hasUnsavedOrAnswer = () => Boolean(textRef.current.trim() || recording || photosRef.current.length > 0 || autosave.isDirty())

  const saveAndClose = () => {
    const shouldSave = hasUnsavedOrAnswer()

    leaveReview()
    autosave.cancel()

    const pendingSave = shouldSave ? save({}, {finished: true}) : Promise.resolve()

    pendingSave.then(() => props.saveAndClose())
  }

  // Revisiting from Review edits one story in place: Save persists the edit
  // and returns to review (republishing the keepsake) rather than advancing
  // to the next question.
  const saveRevisit = () => {
    if (!textRef.current.trim() && !recording && photosRef.current.length === 0) {
      props.toast('Write or speak a little first \u2014 even one line counts.')

      return
    }

    leaveReview()
    autosave.cancel()
    save({}, {finished: true}).then(() => props.saveAndClose())
  }

  // Cancel puts the text back the way it was when the revisit opened; the
  // edits autosave persisted along the way are overwritten with the snapshot.
  // Always submitted: the box matching the snapshot doesn't mean the server
  // does (the writer may have typed and then undone it, with an autosave in
  // between). The draft skips the request when the server already agrees.
  // Flagged as an autosave: undoing autosaves is no reason to republish.
  const cancelRevisit = () => {
    autosave.cancel()
    dropReplacedRecording()
    save({text: revisitTextSnapshotRef.current}, {autosave: true})
    props.cancelRevisit()
  }

  /** Leaving the prompt via the chapter switcher also counts as finishing it, so keep the words. */
  const openChapterSwitch = () => {
    const shouldSave = hasUnsavedOrAnswer()

    leaveReview()
    autosave.cancel()

    if (shouldSave)
      save({}, {finished: true})

    props.openChapterSwitch()
  }

  // play() can reject (decode errors, gesture restrictions — especially when
  // it runs after an async fetch), so `playing` only flips on success — and
  // only if this is still the card's audio: a Record this version surface
  // (or removing the recording) may have torn it down while play() settled,
  // and a late continuation must not show Pause over nothing.
  const startPlayback = () => {
    const audio = audioRef.current

    return audio.play()
      .then(() => {
        if (audioRef.current === audio) setPlaying(true)
        else audio.pause()
      })
      .catch(error => {
        // A rejection from an audio that was torn down meanwhile is the
        // teardown's doing, not a playback problem: nothing to tell.
        if (audioRef.current === audio) throw error
      })
  }

  const openStuckOverlay = () => {
    trackLifeStoryEvent({eventName: 'view_help', userConfig: props.userConfig})
    setShowStuckOverlay(true)
  }

  const playbackFailed = () => props.toast('We couldn\u2019t play that recording just now.')

  /** Plays the saved recording by fetching the decrypted audio as a data URL. */
  const togglePlayback = () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause()
        setPlaying(false)
      } else {
        startPlayback().catch(playbackFailed)
      }

      return
    }

    // A download is already on its way; the click that started it will play
    // the audio when it lands (TK-3078).
    if (pendingPlaybackRef.current) return

    const request = dispatch(readEndpoint(`uploads/${recording['upload-id']}?download=true`))

    pendingPlaybackRef.current = request

    request
      .then(response => {
        // Invalidated while downloading (recording removed or replaced,
        // screen unmounted): this audio must not play.
        if (pendingPlaybackRef.current !== request) return

        pendingPlaybackRef.current = null

        const attributes = response.body.data.attributes

        audioUrlRef.current = `data:${attributes.filetype};base64,${attributes['decrypted-file']}`

        const audio = new Audio(audioUrlRef.current)

        audioRef.current = audio
        audio.ontimeupdate = () => {
          if (audioRef.current !== audio) return

          setPlaybackElapsed(Math.floor(audio.currentTime))
          // Listening back to a long recording is activity too — it shouldn't
          // end in a session-timeout warning (AC 10.1).
          touchSessionActivity()
        }
        audio.onended = () => {
          if (audioRef.current !== audio) return

          setPlaying(false)
          setPlaybackElapsed(0)
        }

        return startPlayback().catch(playbackFailed)
      })
      .catch(() => {
        // An invalidated request's failure is moot — its recording is gone.
        if (pendingPlaybackRef.current !== request) return

        pendingPlaybackRef.current = null
        playbackFailed()
      })
  }

  /* ---------- Refine: handlers ---------- */

  // Remembers the textarea's selection as it loses focus (or as the wand is
  // pressed while it still has focus) so opening the menu can offer
  // "Only your selection".
  const captureSelection = () => {
    const textarea = textareaRef.current

    if (!textarea) return

    capturedSelectionRef.current = {start: textarea.selectionStart, end: textarea.selectionEnd, at: Date.now()}
  }

  // Pressing Back blurs the box before the click lands, and a flush here would
  // save the very words Back is meant to drop (TK-3178). Focus moving to Back
  // shows up as `relatedTarget` (keyboard, most pointers); Safari doesn't
  // focus buttons on click, so the press itself is also noted on pointerdown.
  const handleTextareaBlur = event => {
    captureSelection()

    if (backPressedRef.current || event.relatedTarget === backButtonRef.current) return

    autosave.flush()
  }

  const noteBackPressed = () => { backPressedRef.current = true }

  const forgetBackPressed = () => { backPressedRef.current = false }

  const handleWandPointerDown = () => {
    if (document.activeElement === textareaRef.current) captureSelection()
  }

  const selectionForMenu = () => {
    const captured = capturedSelectionRef.current

    if (!captured || captured.end <= captured.start || Date.now() - captured.at > SELECTION_WINDOW_MS) return null

    const passage = textRef.current.slice(captured.start, captured.end)
    const words = countWords(passage)

    if (words < MIN_SELECTION_WORDS) return null

    return {start: captured.start, end: captured.end, words, length: passage.trim().length}
  }

  const openRefineMenu = () => {
    if (refine.status !== REFINE_STATUS.IDLE) return

    setMenuSelection(selectionForMenu())
    setMenuOpen(true)
  }

  const closeRefineMenu = useCallback(({returnFocus = false} = {}) => {
    setMenuOpen(false)

    if (returnFocus && wandRef.current) wandRef.current.focus()
  }, [])

  // Enrich reads the whole story, whatever is selected: the questions come
  // from all of it and the finishing rewrite is one piece.
  const chooseRefine = ({mode, instruction}) => {
    if (mode === API_MODES.ENRICH) {
      refine.showNote(null)
      setInterviewTakeover(isSheetLayout())
      interview.start()

      return
    }

    focusAfterIdleRef.current = 'wand'
    refine.refine({
      mode,
      instruction,
      selection: menuSelection ? {start: menuSelection.start, end: menuSelection.end} : null
    })
  }

  const undoRefine = () => {
    focusAfterIdleRef.current = 'wand'
    refine.undo()
  }

  // The mini Undo/Redo work while idle too, where no status change moves
  // focus; when the one just used runs out and goes disabled, focus would
  // be stranded on it, so it moves to the wand instead.
  useEffect(() => {
    const active = document.activeElement

    if (active && active.disabled && active.classList.contains('ls-mini-btn') && wandRef.current) wandRef.current.focus()
  }, [refine.canUndo, refine.canRedo])

  // There is no "Keep it" button: clicking back into the story while a result
  // is up for review keeps it and closes the bar, with focus landing where
  // the click put it. Dragging the textarea taller by its resize grip is
  // not that click: the review bar stays put.
  const handleTextareaPointerDown = event => {
    if (refine.status !== REFINE_STATUS.REVIEW || onResizeGrip(event.currentTarget, event)) return

    focusAfterIdleRef.current = null
    refine.keep()
  }

  const retryRefine = () => {
    focusAfterIdleRef.current = 'wand'
    refine.tryAgain()
  }

  // Done: the review closes and the writer is back in their story, at its
  // end, free to ask for another change.
  const doneReviewing = () => {
    focusAfterIdleRef.current = 'textarea'
    refine.keep()
  }

  /* ---------- Enrich interview: failures, Escape, focus ---------- */

  // A failure to get the questions ends the interview (nothing to go back
  // to) with a note and Try again. A failed write on desktop drops back to
  // the questions with the answers kept, behind a note; the takeover shows
  // its own error screen instead.
  useEffect(() => {
    if (interview.phase !== INTERVIEW_PHASE.ERROR || !interview.error) return

    const {step, retry} = interview.error

    // Try again clears the note first, so it isn't left up while the
    // interview is reading or writing again.
    const retrying = action => () => {
      refine.showNote(null)
      action()
    }

    if (step === INTERVIEW_STEP.QUESTIONS) {
      const message = interviewTakeover && retry ? INTERVIEW_NOTES.questionsFailedTakeover : interview.errorMessage

      interview.cancel({silent: true})
      refine.showNote({kind: 'error', code: 'questions_failed', message, retry, onRetry: retry ? retrying(interview.start) : undefined})
    } else if (!interviewTakeover) {
      interview.backToQuestions()
      refine.showNote({kind: 'error', code: 'write_failed', message: interview.errorMessage, retry, onRetry: retry ? retrying(interview.write) : undefined})
    }
  }, [interview.phase, interview.error])

  // Escape leaves the interview with the story as it was (the takeover
  // listens for itself: it may be mid-recording).
  useEffect(() => {
    if (!interviewing || interviewTakeover) return undefined

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault()
        interview.cancel()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [interviewing, interviewTakeover, interview.cancel])

  // When the interview ends, focus returns to the wand it started from.
  const wasInterviewingRef = useRef(interviewing)

  useEffect(() => {
    if (wasInterviewingRef.current && !interviewing && wandRef.current) wandRef.current.focus()

    wasInterviewingRef.current = interviewing
  }, [interviewing])

  // Focus follows the refine (AC 4.6, 9.9): Stop while it runs, Undo once
  // there's a result to judge, and back to where the writer left off after.
  const previousRefineStatusRef = useRef(refine.status)

  useEffect(() => {
    const previous = previousRefineStatusRef.current

    previousRefineStatusRef.current = refine.status

    if (refine.status === REFINE_STATUS.GENERATING) {
      if (stopButtonRef.current) stopButtonRef.current.focus()
    } else if (refine.status === REFINE_STATUS.REVIEW) {
      if (undoButtonRef.current) undoButtonRef.current.focus()
    } else if (previous !== REFINE_STATUS.IDLE) {
      const target = focusAfterIdleRef.current

      focusAfterIdleRef.current = null

      if (target === 'textarea' && textareaRef.current) {
        const textarea = textareaRef.current

        textarea.focus()
        textarea.setSelectionRange(textarea.value.length, textarea.value.length)
      } else if (target === 'wand' && wandRef.current) {
        wandRef.current.focus()
      }
    }
  }, [refine.status])

  // Paused counts as recording everywhere but the timer: the mic is held,
  // the text is read-only, and Stop still saves what was recorded.
  const isPaused = recorderState === RECORDER_STATES.PAUSED
  const isRecording = isLiveRecorderState(recorderState)
  const isProcessing = recorderState === RECORDER_STATES.PROCESSING
  // At the cap the textarea's maxLength swallows keystrokes without a trace,
  // so typing gets a persistent inline message rather than a one-shot toast —
  // it stays as long as the answer is full and clears once text is trimmed
  // (TK-3018). Dictation reaching the cap keeps its own toast on top of this.
  const atMaxLength = text.length >= MAX_STORY_LENGTH
  // Leaving the prompt mid-recording (or before the upload finishes) would
  // unmount before onRecordingComplete persists the recording, silently losing
  // it — so every way out of the screen waits for the recorder to settle. A
  // Start still waiting on the microphone prompt counts: the mic is spoken
  // for, so nothing else (the wand, the second recorder) may claim it.
  const recordingBusy = isRecording || isProcessing || recorderStarting
  // A refine in flight holds the exits too, and the mic with them: a
  // recording started mid-refine would rebuild the textarea from the
  // dictation base and clobber the result when it landed (AC 4.3). So does
  // the interview: leaving would drop the answers.
  // A photo still uploading holds them too: leaving would lose it before
  // it's saved to the story.
  const busy = recordingBusy || generating || interviewing || storyPhotos.busy
  // The refine bars carry no save state (prototype); only a failure, with its
  // Retry, is kept in view there.
  const saveFailed = props.saveStatus === SAVE_STATUS.ERROR || props.saveStatus === SAVE_STATUS.UNAUTHORIZED
  // The wand appears once there is a word to refine and the recorder is
  // quiet (AC 1.1, 1.2); it isn't rendered at all when the flag is off (7.4).
  const wandVisible = refineEnabled && countWords(text) > 0 && !recordingBusy

  // The saved recording was made over a different refine version than the
  // one the words stand on now (TK-3234): a refine or Try again since, or
  // an older take put back. Undo/Redo onto the version it was made over
  // makes it current again.
  const refinedSinceRecording = Boolean(recording) && versionOfTake(recording) !== refine.version
  // The read-along recorder reads the story back, so it needs words to read;
  // a refined story emptied out since falls back to dictation.
  const recordsRefinedVersion = refinedSinceRecording && countWords(text) > 0

  const recordButtonLabel = () => {
    if (isPaused) return `Paused at ${formatDuration(duration)} \u2014 tap when you\u2019re done`
    if (isRecording) return `Recording ${formatDuration(duration)} \u2014 tap when you\u2019re done`
    if (isProcessing) return 'Keeping your words safe\u2026'
    if (recordsRefinedVersion) return 'Record this version'
    if (recording) return 'Say it again'

    // "instead" drops below $tabletSm, where the foot is tight on room.
    return <>Speak it<span className='ls-rec-label-long'> instead</span></>
  }

  // One record button: "Record this version" opens the read-along recorder
  // over the refined text; the other two labels run the inline dictation
  // recorder (through the replace confirmation when a recording exists), and
  // a live recording is stopped either way.
  const handleRecordButtonClick = () => {
    // The takeover runs its own recording screens over this one.
    if (interview.running) return

    if (recordsRefinedVersion && recorderState === RECORDER_STATES.IDLE && !recorderStarting) {
      recordVersion.openRecorder()

      return
    }

    toggleRecording()
  }

  // The review bar offers "Record this version" alongside the result, for a
  // story with words in it. Not while the dictation recorder is live: two
  // recorders on one mic would race each other's take and upload.
  const canRecordVersion = recordingSupported && !recordingBusy && refine.hasHistory && countWords(text) > 0 &&
    refine.status === REFINE_STATUS.REVIEW
  const recordVersionButton = canRecordVersion ?
    (<button className='ls-txt-btn ls-rv-btn' type='button' onClick={recordVersion.openRecorder}>
      <MicIcon size={16} />Record this version
    </button>) :
    null

  const photoCount = storyPhotos.photos.length + storyPhotos.uploading.length
  const photoButtonLabel = photoCount > 0 ? `Add photos, ${photoCount} of ${MAX_STORY_PHOTOS} added` : 'Add photos'

  const durationLabel = playing ?
    `${formatDuration(playbackElapsed)} / ${formatDuration(recording && recording.duration)}` :
    formatDuration(recording && recording.duration)

  return (
    <div className='prompt-screen'>
      <div className={`ls-top${showsPrints ? ' ls-top-over-label' : ''}`} ref={topBarRef}>
        {
          // A revisit finishes through the Cancel/Save pair below, not "Save
          // and close" (TK-3061); the empty span keeps "Feeling stuck?" on
          // the right.
          props.revisiting ?
            <span /> :
            <button className='ls-exit' disabled={props.publishing || busy} onClick={saveAndClose}>
              {props.publishing ? 'Saving to your Everplan\u2026' : 'Save and close'}
            </button>
        }
        {
          // Not while refining: when the result lands, focus moves to Undo,
          // which would break out of the overlay's modal focus trap.
        }
        <button className='ls-link-quiet' disabled={generating || interviewing} onClick={openStuckOverlay}>Feeling stuck?</button>
      </div>
      <div className={`ls-prompt${menuOpen ? ' ls-prompt-menu-open' : ''}`}>
        <button aria-label='Choose a different chapter' className='ls-chapter-switch' disabled={busy} ref={chapterLabelRef} onClick={openChapterSwitch}>
          <span>{chapter.name}</span>
          <CaretIcon />
        </button>
        <StoryPhotoPrints
          focusWhenEmpty={focusAfterLastPhotoRemoved}
          images={photoImages}
          photos={storyPhotos.photos}
          uploading={storyPhotos.uploading}
          onRemove={index => storyPhotos.removePhoto(storyPhotos.photos[index]['upload-id'])}
          onSwap={storyPhotos.swapPhotos}
          onView={openPhotoViewer}
        />
        <h2 className='ls-question' ref={questionRef} tabIndex={-1}>{prompt.question}</h2>
        {
          recording && !isRecording &&
            <div className={`ls-voice-chip${draftRecording ? ' multi' : ''}`}>
              <button aria-label={playing ? 'Pause recording' : 'Play recording'} className='ls-voice-play' onClick={togglePlayback}>
                {playing ? <PauseIcon size={14} /> : <PlayIcon size={14} />}
              </button>
              <span className='ls-vc-body'>
                <span><b>Your voice is saved with this story.</b></span>
                {
                  // Not while the dictation recorder is live or saving: a take
                  // landing after Keep/Use here would overwrite the choice.
                  draftRecording && !recordingBusy &&
                    <span className='ls-vc-draft'>
                      <span>You also have a new recording ({formatDuration(draftRecording.duration)}) that isn&rsquo;t being used yet.</span>
                      <button className='ls-vc-review' type='button' onClick={() => recordVersion.openDecision(draftRecording)}>Review it</button>
                    </span>
                }
              </span>
              <span className='ls-voice-actions'>
                <span className='ls-voice-duration'>{durationLabel}</span>
                {
                  // Not while the dictation recorder is live or saving either:
                  // the take landing after a removal would put a recording back
                  // over it, and the two saves could land in either order.
                }
                <button aria-label='Remove voice attachment' className='ls-voice-remove' disabled={recordingBusy} onClick={removeRecording}>
                  <TrashIcon />
                </button>
              </span>
            </div>
        }
        {
          isPaused &&
            <div className='ls-transcribing'>Paused. Pick up where you left off whenever you&rsquo;re ready.</div>
        }
        {
          ((isRecording && !isPaused) || isProcessing) &&
            <div className='ls-transcribing'>
              <span className='ls-spin' /> Writing down your words&hellip;
            </div>
        }
        {
          nearingCap &&
            <div className='ls-cap-warning' role='status'>
              You&rsquo;re nearing the max recording length of 10 min.
            </div>
        }
        {
          atMaxLength &&
            <div className='ls-cap-warning ls-cap-error' role='status'>
              You&rsquo;ve reached the max character length.
            </div>
        }
        <div className={`ls-write-card${generating ? ' refining thinking' : ''}${menuOpen ? ' menu-open' : ''}${interviewing ? ' interviewing' : ''}`} ref={writeCardRef}>
          <label className='sr-only' htmlFor='life-story-answer'>Your answer</label>
          <textarea
            id='life-story-answer'
            ref={textareaRef}
            hidden={refine.showingChanges || (interviewing && !interviewTakeover)}
            maxLength={MAX_STORY_LENGTH}
            placeholder={answerPlaceholder()}
            // Read-only while dictating: transcript updates rebuild the text from
            // dictationBaseRef, which would silently drop anything typed mid-recording.
            // And while refining: the result replaces the box wholesale when it lands.
            readOnly={isRecording || isProcessing || generating}
            value={interimText ? `${text}${interimText}` : text}
            onBlur={handleTextareaBlur}
            onChange={handleTextChange}
            onPointerDown={handleTextareaPointerDown}
          />
          {refine.showingChanges && <RefineDiffView after={text} before={refine.previousText} />}
          {interviewing && !interviewTakeover && <EnrichInterviewView interview={interview} text={text} />}
          <RefineNote note={refine.note} retryDisabled={recordingBusy} onRetry={retryRefine} />
          {
            interviewing && !interviewTakeover &&
              <EnrichInterviewFooter
                answeredCount={interview.answeredCount}
                canWrite={interview.canWrite}
                cancelRef={interviewCancelRef}
                phase={interview.phase}
                total={(interview.items || []).length}
                onCancel={interview.cancel}
                onKeep={interview.cancel}
                onUse={interview.use}
                onWrite={interview.write}
              />
          }
          {
            (!interviewing || interviewTakeover) && (refine.status === REFINE_STATUS.IDLE ?
              // The save status sits opposite the mic, on the card it describes.
              <div className='ls-write-foot'>
                <div className='ls-write-tools'>
                  {
                    recordingSupported &&
                      <button className={`ls-rec-btn${isRecording ? ' recording' : ''}${isPaused ? ' rec-paused' : ''}`} disabled={isProcessing || generating} onClick={handleRecordButtonClick}>
                        {isRecording && !isPaused && <span className='ls-rec-dot' />}
                        <MicIcon size={16} />
                        <span>{recordButtonLabel()}</span>
                      </button>
                  }
                  {
                    isRecording &&
                      <button
                        aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
                        className='ls-pause-btn small'
                        type='button'
                        onClick={isPaused ? resumeRecording : pauseRecording}>
                        {isPaused ? <PlayIcon size={16} /> : <PauseIcon size={16} />}
                        <span>{isPaused ? 'Resume' : 'Pause'}</span>
                      </button>
                  }
                  <button
                    aria-disabled={storyPhotos.atLimit ? true : null}
                    aria-label={photoButtonLabel}
                    className='ls-rec-btn ls-photo-btn'
                    disabled={generating || interviewing}
                    ref={addPhotosButtonRef}
                    type='button'
                    onClick={openPhotoPicker}>
                    <PhotoIcon size={16} />
                    <span className='ls-photo-label-long'>Add photos</span>
                    <span className='ls-photo-label-short'>Photo</span>
                    {photoCount > 0 && <span className='ls-photo-count'>{photoCount}/{MAX_STORY_PHOTOS}</span>}
                  </button>
                  <input accept={STORY_PHOTO_ACCEPT} hidden multiple ref={photoInputRef} type='file' onChange={handlePhotoFiles} />
                  {
                    wandVisible &&
                      <RefineMenu
                        arrive
                        open={menuOpen}
                        ref={wandRef}
                        selection={menuSelection ? {words: menuSelection.words, length: menuSelection.length} : null}
                        textLength={text.length}
                        wordCount={countWords(text)}
                        onChoose={chooseRefine}
                        onClose={closeRefineMenu}
                        onOpen={openRefineMenu}
                        onWandPointerDown={handleWandPointerDown}
                      />
                  }
                  {
                    wandVisible && refine.hasHistory &&
                      <div className='ls-mini-group'>
                        <div className='ls-wand-wrap'>
                          <button aria-describedby='ls-tip-undo' aria-label='Undo refine' className='ls-icon-btn ls-mini-btn' disabled={!refine.canUndo} type='button' onClick={undoRefine}>
                            <UndoIcon />
                          </button>
                          <span className='ls-tip' id='ls-tip-undo' role='tooltip'>Undo</span>
                        </div>
                        <div className='ls-wand-wrap'>
                          <button aria-describedby='ls-tip-redo' aria-label='Redo refine' className='ls-icon-btn ls-mini-btn' disabled={!refine.canRedo} type='button' onClick={refine.redo}>
                            <RedoIcon />
                          </button>
                          <span className='ls-tip' id='ls-tip-redo' role='tooltip'>Redo</span>
                        </div>
                      </div>
                  }
                </div>
                <SaveIndicator lastSavedAt={props.lastSavedAt} retry={props.retrySave} status={props.saveStatus} />
              </div> :
              // Keyed by status so a Try again from the review bar mounts a
              // fresh footer: its "Reading your story…" label starts over
              // instead of flashing the mode label for a frame.
              <RefineFooter
                key={refine.status}
                canRedo={refine.canRedo}
                canRetry={refine.canRetry}
                canShowChanges={refine.canShowChanges}
                canUndo={refine.canUndo}
                label={refine.label}
                mode={refine.mode}
                scope={refine.scope}
                showDone={DONE_MODES.includes(refine.mode)}
                showingChanges={refine.showingChanges}
                status={refine.status}
                recordVersion={recordVersionButton}
                stopRef={stopButtonRef}
                undoRef={undoButtonRef}
                saveFailure={saveFailed ? <SaveIndicator lastSavedAt={props.lastSavedAt} retry={props.retrySave} status={props.saveStatus} /> : null}
                onDone={doneReviewing}
                onRedo={refine.redo}
                onStop={refine.stop}
                onToggleChanges={refine.toggleChanges}
                onTryAgain={retryRefine}
                onUndo={undoRefine}
              />)
          }
        </div>
        <div aria-live='polite' className='sr-only' key={refine.announcement.key}>{refine.announcement.text}</div>
        {
          showStarters &&
            <div className='ls-starters'>
              <span className='ls-starters-label'>A few ways to get started</span>
              {
                prompt.starters.map(starter => (
                  <button className='ls-starter-chip' key={starter} onClick={() => applyStarter(starter)}>
                    {starter}
                  </button>
                ))
              }
            </div>
        }
        {/* The page's own Back/Next step aside while a refine is running or the desktop interview is up (prototype); the takeover covers them. */}
        <div className='ls-prompt-actions' hidden={generating || (interviewing && !interviewTakeover)}>
          {
            props.revisiting ?
              <>
                <button className='ls-btn ls-btn-outline' disabled={busy} onClick={cancelRevisit}>Cancel</button>
                <button className='ls-btn ls-btn-primary' disabled={busy} onClick={saveRevisit}>Save</button>
              </> :
              <>
                <button
                  className='ls-btn ls-btn-outline'
                  disabled={busy}
                  ref={backButtonRef}
                  onClick={back}
                  onPointerCancel={forgetBackPressed}
                  onPointerDown={noteBackPressed}
                  onPointerLeave={forgetBackPressed}>
                  Back
                </button>
                <button className='ls-btn ls-btn-primary' disabled={busy} onClick={next}>Next</button>
              </>
          }
        </div>
      </div>
      {
        interviewing && interviewTakeover &&
          <EnrichTakeover
            currentRecording={recording}
            finePointer={hasFinePointer()}
            interview={interview}
            recordVersion={recordingSupported ? recordVersion : null}
            storyText={text}
          />
      }
      {recordVersion.open && <RecordVersionModal currentRecording={recording} recordVersion={recordVersion} text={text} />}
      {showStuckOverlay && <StuckOverlay close={() => setShowStuckOverlay(false)} />}
      {viewingPhoto && <StoryPhotoViewer controls={false} covers={photoImages} photos={[viewingPhoto]} sources={fullPhotos} onClose={closePhotoViewer} />}
      {
        confirmingReplace &&
          <div aria-labelledby='ls-replace-title' aria-modal='true' className='ls-overlay' role='dialog'>
            <div className='ls-overlay-card'>
              <h3 id='ls-replace-title'>Replace your voice recording?</h3>
              <p>Please note, this will replace your current voice attachment. Your written words stay as they are.</p>
              <div className='ls-overlay-actions'>
                <button className='ls-link-quiet' onClick={() => setConfirmingReplace(false)}>Cancel</button>
                <button className='ls-btn ls-btn-primary ls-btn-sm' onClick={confirmReplace}>Replace and record</button>
              </div>
            </div>
          </div>
      }
    </div>
  )
}

PromptScreen.propTypes = {
  advance: PropTypes.func,
  back: PropTypes.func,
  cancelRevisit: PropTypes.func,
  chapter: PropTypes.object,
  lastSavedAt: PropTypes.number,
  logEvent: PropTypes.func,
  openChapterSwitch: PropTypes.func,
  prompt: PropTypes.object,
  publishing: PropTypes.bool,
  retrySave: PropTypes.func,
  revisiting: PropTypes.bool,
  saveAndClose: PropTypes.func,
  saveAnswer: PropTypes.func,
  saveStatus: PropTypes.string,
  story: PropTypes.object,
  toast: PropTypes.func,
  userConfig: PropTypes.object
}

export default PromptScreen
